import React, { FCC, useEffect, useState } from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import {
  useModalContext,
  useStripe,
  OrderedBreadcrumbs,
  LoadingState,
} from '@sketch/components'

import {
  DynamicLoadingPage,
  routes,
  useQueryParams,
} from '@sketch/modules-common'

import { WorkspaceSubscribePaymentDetails } from '../../pages'
import PartnerSubscriptionDoneModal from '../../modals/PartnerSubscriptionDoneModal'

import useWorkspaceSubscribeState from '../WorkspaceSubscribeView/WorkspaceSubscribeView.hooks'
import { isTeamYearlyPlan, isWorkspaceSubscriptionActive } from '../../utils'

import { BreadcrumbsWrapper } from '../../views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// GQL

import {
  useAcceptPartnershipInviteMutation,
  useCreatePartnerWorkspaceMutation,
  useGetPendingWorkspaceMembershipQuery,
  useGetAvailablePlansQuery,
  GetWorkspacesDocument,
  CloudBillingPlanFragment,
  useGetCustomerForPartnerSubscribeQuery,
  useGetPartnerBillingDetailsQuery,
  GetUserSettingsDocument,
} from '@sketch/gql-types'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

import { WorkspaceSubscribeBadge } from '../../components/WorkspaceSubscribeBadge'
import { IndexLayoutError } from '@sketch/modules-common/src/components/IndexLayoutOld/IndexLayoutError'

import { WorkspaceSubscribeLayout } from '../../components/WorkspaceSubscribeLayout'
import { SubscribeToPlanState } from '../../types'
import PartnerExpiredWorkspaceError from '../../components/PartnerExpiredWorkspaceError'
import { useGetClientCurrency } from '../../operations/useClientCurrency'

type WorkspaceCreateType = {
  name: string
  logo: File | undefined
  ownerEmail: string
}

const PARTNER_WORKSPACE_CRUMBS = [
  { content: 'Create Workspace' },
  { content: 'Set Up a Subscription' },
]

interface BasicWorkspaceInfo {
  name: string
  avatar?: string | null
}

type FormValues = Parameters<
  React.ComponentProps<typeof WorkspaceSubscribePaymentDetails>['onSubmit']
>[0]

type FormSubmit = (
  state: SubscribeToPlanState,
  values: FormValues
) => Promise<any>

interface PartnerSubscribeProps {
  plan: CloudBillingPlanFragment
  currency: Currency
  workspace: BasicWorkspaceInfo
  isCreatingWorkspace: boolean
  numberOfEditorSeats: number
  onSubmit: FormSubmit
}

const PartnerSubscribe = (props: PartnerSubscribeProps) => {
  const {
    plan,
    currency,
    isCreatingWorkspace,
    numberOfEditorSeats,
    workspace,
    onSubmit,
  } = props

  const [state, triggers] = useWorkspaceSubscribeState(currency)

  const { loading, data, error } = useGetPartnerBillingDetailsQuery({
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    triggers.setEditors(numberOfEditorSeats)
  }, [numberOfEditorSeats, triggers])

  useEffect(() => {
    triggers.setCurrency(currency)
  }, [currency, triggers])

  if (loading) {
    return <LoadingState />
  }

  if (error || !data?.partnerBillingDetails) {
    return <IndexLayoutError />
  }

  const partnerBillingDetails = data.partnerBillingDetails

  return (
    <>
      <BreadcrumbsWrapper>
        <WorkspaceSubscribeBadge
          name={workspace.name}
          avatarSrc={workspace.avatar || undefined}
        />

        {isCreatingWorkspace && (
          <OrderedBreadcrumbs
            showAllInMobile
            crumbs={PARTNER_WORKSPACE_CRUMBS}
            currentCrumb={1}
          />
        )}
      </BreadcrumbsWrapper>

      <WorkspaceSubscribePaymentDetails
        title={!isCreatingWorkspace ? 'Set Up a Subscription' : ''}
        paymentMethod="invoice"
        variant="partner"
        isCreatingWorkspace={isCreatingWorkspace}
        billingInfo={{
          address: partnerBillingDetails?.address?.line1,
          city: partnerBillingDetails?.address?.city,
          postalCode: partnerBillingDetails?.address?.postalCode,
          state: partnerBillingDetails?.address?.state || '',
          country: partnerBillingDetails?.address?.country,
          email: partnerBillingDetails?.email,
          name: partnerBillingDetails?.name,
          taxId: partnerBillingDetails?.taxId || undefined,
          tos: false,
        }}
        billSimulation={{
          plan,
          trialEnd: '',
          numberEditors: state.editors || 0,
          onEditorsChange: triggers.setEditors,
          numberExtraSeats: 0,
          showDiscountInput: false,
          discountCode: state.discountCode,
          onValidDiscountCode: triggers.applyDiscountCode,
          onClearDiscountCode: triggers.clearDiscountCode,
          discountError: state.discountError,
          onUpdateDiscountError: triggers.updateDiscountError,
          currency: state.currency,
        }}
        onSubmit={values => onSubmit(state, values)}
      />
    </>
  )
}

interface CreateNewWorkspaceAsPartnerProps {
  locationState: WorkspaceCreateType
  plan: CloudBillingPlanFragment
}

const CreateNewWorkspaceAsPartner = ({
  locationState,
  plan,
}: CreateNewWorkspaceAsPartnerProps) => {
  const history = useHistory()
  const { showModal, hideModal } = useModalContext()

  const currencyData = useGetClientCurrency()
  const [selectedCurrency, setCurrency] = useState<Currency | null>(null)

  const [createPartnerWorkspace] = useCreatePartnerWorkspaceMutation({
    onCompleted: props => {
      history.push(
        routes.WORKSPACE_SETTINGS_PEOPLE.create({
          workspaceId: props.createPartnerWorkspace?.workspace?.identifier,
        })
      )

      showModal(
        PartnerSubscriptionDoneModal,
        { onHide: hideModal, workspaceName: locationState.name },
        { closeOnRouteChange: false }
      )
    },
    refetchQueries: [{ query: GetWorkspacesDocument }],
    onError: 'show-toast',
  })

  const defaultCurrency = currencyData.data?.currencies.default

  useEffect(() => {
    defaultCurrency && setCurrency(defaultCurrency)
  }, [defaultCurrency])

  if (currencyData.loading || !selectedCurrency) {
    return <DynamicLoadingPage />
  }

  if (currencyData.error) {
    return <IndexLayoutError />
  }

  return (
    <WorkspaceSubscribeLayout
      selectedCurrency={selectedCurrency}
      onCurrencyChange={setCurrency}
    >
      <PartnerSubscribe
        currency={selectedCurrency}
        plan={plan}
        isCreatingWorkspace
        numberOfEditorSeats={1}
        workspace={{ name: locationState.name }}
        onSubmit={async (state, values) => {
          if (!('shippingName' in values)) {
            return
          }

          return await createPartnerWorkspace({
            variables: {
              input: {
                seats: state.editors as number,
                tosAgreed: values.tos as boolean,
                ownerEmail: locationState.ownerEmail,
                avatar: locationState.logo,
                name: locationState.name,
                currency: selectedCurrency,
                shipping: {
                  name: values.shippingName,
                  address: {
                    state: values.shippingState,
                    line1: values.shippingAddress,
                    city: values.shippingCity,
                    postalCode: values.shippingPostalCode,
                    country: values.shippingCountry,
                  },
                },
              },
            },
          })
        }}
      />
    </WorkspaceSubscribeLayout>
  )
}

interface JoinWorkspaceAsPartnerTokenProps {
  token: string
  plan: CloudBillingPlanFragment
}

interface JoinWorkspaceAsPartnerMembershipIdProps {
  membershipId: string
  plan: CloudBillingPlanFragment
}

type JoinWorkspaceAsPartnerProps =
  | JoinWorkspaceAsPartnerTokenProps
  | JoinWorkspaceAsPartnerMembershipIdProps

const JoinWorkspaceAsPartner = (props: JoinWorkspaceAsPartnerProps) => {
  const { plan, ...otherProps } = props

  const history = useHistory()
  const { showModal, hideModal } = useModalContext()

  const membershipToken = 'token' in otherProps ? otherProps.token : undefined
  const membershipId =
    'membershipId' in otherProps ? otherProps.membershipId : undefined

  const pendingInviteData = useGetPendingWorkspaceMembershipQuery({
    variables: { membershipToken, membershipId, includeCustomer: true },
  })

  const customerId =
    pendingInviteData.data?.pendingWorkspaceMembership.workspace.customer
      ?.identifier

  const workspace = pendingInviteData.data?.pendingWorkspaceMembership.workspace
  const [acceptPartnershipInvite] = useAcceptPartnershipInviteMutation({
    onCompleted: () => {
      if (workspace) {
        history.push(
          routes.WORKSPACE_SETTINGS_PEOPLE.create({
            workspaceId: workspace.identifier,
          })
        )

        showModal(
          PartnerSubscriptionDoneModal,
          { onHide: hideModal, workspaceName: workspace.name },
          { closeOnRouteChange: false }
        )
      }
    },
    onError: 'show-toast',
    refetchQueries: [
      { query: GetUserSettingsDocument },
      { query: GetWorkspacesDocument },
    ],
    awaitRefetchQueries: true,
  })

  const customerPlanQuery = useGetCustomerForPartnerSubscribeQuery({
    variables: { customerId: customerId || '' },
    skip: !customerId,
    fetchPolicy: 'network-only',
  })

  if (customerPlanQuery.loading || pendingInviteData.loading) {
    return <DynamicLoadingPage />
  }

  if (
    customerPlanQuery.error ||
    pendingInviteData.error ||
    !customerPlanQuery.data ||
    !pendingInviteData.data ||
    !workspace
  ) {
    return <IndexLayoutError />
  }

  const subscriptionInfo = customerPlanQuery.data.customer?.subscriptionInfo

  if (subscriptionInfo.__typename !== 'CloudSubscription') {
    return <IndexLayoutError />
  }

  if (!isWorkspaceSubscriptionActive(subscriptionInfo.status)) {
    return <PartnerExpiredWorkspaceError />
  }

  const currency =
    customerPlanQuery.data.customer?.billingSummary.current.currency

  const numberOfScheduledSeats = subscriptionInfo?.scheduledSeatsTotal
  const numberOfCurrentSeats = subscriptionInfo?.currentSeatsTotal
  const numberOfEditors = numberOfScheduledSeats || numberOfCurrentSeats

  return (
    <WorkspaceSubscribeLayout
      selectedCurrency={currency}
      /**
       * TODO: Allow the partner to set the desired currency if the workspace its joining is on trial
       *
       * Given a trial workspace don't have a set currency, we should allow the partner to choose the
       * convenient currency (if more then one) when joining the workspace
       *
       * We have opted to not ship this initially, given we need BE API support for this change
       * https://linear.app/sketch/issue/WEB-1270/enable-fe-to-send-the-currency-when-a-partner-is-joining-a-trial
       */
      onCurrencyChange={() => {}}
      hideCurrencySelector
    >
      <PartnerSubscribe
        plan={plan}
        currency={currency}
        numberOfEditorSeats={numberOfEditors}
        isCreatingWorkspace={false}
        workspace={{
          name: workspace?.name,
          avatar: workspace.avatar?.small,
        }}
        onSubmit={async (state, values) => {
          if (!('shippingName' in values)) {
            return
          }

          return acceptPartnershipInvite({
            variables: {
              membershipId,
              membershipToken,
              seats: state.editors!,
              tosAgreed: values.tos,
              shipping: {
                name: values.shippingName,
                address: {
                  state: values.shippingState,
                  line1: values.shippingAddress,
                  city: values.shippingCity,
                  postalCode: values.shippingPostalCode,
                  country: values.shippingCountry,
                },
              },
            },
          })
        }}
      />
    </WorkspaceSubscribeLayout>
  )
}

const WorkspacePartnerSubscribeView: FCC = () => {
  const { token, membershipId } =
    useQueryParams<'WORKSPACE_SUBSCRIBE_PARTNER'>()
  const location = useLocation<WorkspaceCreateType>()

  // Load stripe in order previously
  const { load: loadStripe, status: stripeStatus } = useStripe()

  const planQuery = useGetAvailablePlansQuery()
  const {
    loading: loadingPlans,
    error: errorPlans,
    data: plansData,
  } = planQuery

  const plans = plansData?.availablePlans

  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  if (stripeStatus === 'pending' || loadingPlans) {
    return <DynamicLoadingPage />
  }

  if (stripeStatus === 'error' || errorPlans || !plans) {
    return <IndexLayoutError />
  }

  const partnerPlan = plans?.find(isTeamYearlyPlan)

  if (!partnerPlan) {
    return <IndexLayoutError />
  }

  /**
   * If "token" is present this means the partner is being invited
   * to a workspace
   */
  if (token) {
    return <JoinWorkspaceAsPartner token={token} plan={partnerPlan} />
  }

  /**
   * If "membershipId" is present this means the partner accepting
   * the membership via its workspace settings
   */
  if (membershipId) {
    return (
      <JoinWorkspaceAsPartner membershipId={membershipId} plan={partnerPlan} />
    )
  }

  /**
   * If the base partner data is on the URL location
   * this means the partner is creating the workspace by setting
   * the owner email
   */
  if (location.state) {
    return (
      <CreateNewWorkspaceAsPartner
        locationState={location.state}
        plan={partnerPlan}
      />
    )
  }

  /**
   * If somehow the data is not present or an error occurs we redirect the
   * user to the workspace settings
   */
  return <Redirect to={routes.WORKSPACE_AGNOSTIC_SETTINGS.create({})} />
}

export default WorkspacePartnerSubscribeView
